import { getContrastTextColor } from '@sportninja/common/utils/colorContrast'

export interface EventCategory {
  label: string
  /** Sent to the API verbatim — value and label are the same string. */
  value: string
  /** Badge fill. Hex, deliberately NOT from appColors (see below). */
  color: string
}

export interface ResolvedEventCategory extends EventCategory {
  /**
   * Black or white, whichever reads on `color` — computed rather than assumed,
   * via the same `getContrastTextColor` the game scoreboard uses.
   *
   * Bright saturated fills (Warm-Up cyan, Fundraiser gold) correctly take
   * BLACK text — white on those fails WCAG AA outright.
   */
  textColor: string
}

/**
 * Suggested event categories, mirroring the backend's `SUGGESTED_CATEGORIES`.
 *
 * Order is meaningful — grouped by kind (on-field, assessment, off-field prep,
 * admin, community, logistics, then Other) rather than alphabetised, so the
 * select reads as a short menu instead of a long list.
 *
 * The colours are literal hex rather than `appColors` tokens on purpose: they
 * are a categorical palette that must stay distinguishable from each other,
 * not part of the tenant theme. Pulling them from the theme would make two
 * categories collide the moment a token changed, and they must look the same
 * on SportNinja and Canlan.
 */
export const EVENT_CATEGORIES: EventCategory[] = [
  // On-ice / on-field
  { label: 'Practice', value: 'Practice', color: '#2563EB' },
  { label: 'Skills Session', value: 'Skills Session', color: '#3B82F6' },
  { label: 'Scrimmage', value: 'Scrimmage', color: '#0EA5E9' },
  { label: 'Warm-Up', value: 'Warm-Up', color: '#06B6D4' },
  // Assessment
  { label: 'Tryout', value: 'Tryout', color: '#7C3AED' },
  { label: 'Evaluation', value: 'Evaluation', color: '#A855F7' },
  // Off-ice preparation
  { label: 'Conditioning', value: 'Conditioning', color: '#16A34A' },
  { label: 'Video Session', value: 'Video Session', color: '#65A30D' },
  // Admin
  { label: 'Team Meeting', value: 'Team Meeting', color: '#D97706' },
  // Community & social
  { label: 'Photo Day', value: 'Photo Day', color: '#DB2777' },
  // Gold rather than orange: orange-600 (#EA580C) is red-dominant enough to be
  // mistaken for a cancelled badge at a glance.
  { label: 'Fundraiser', value: 'Fundraiser', color: '#EAB308' },
  { label: 'Team Social', value: 'Team Social', color: '#C026D3' },
  // Logistics
  { label: 'Travel', value: 'Travel', color: '#78716C' },
  // Anything typed that we do not recognise
  { label: 'Other', value: 'Other', color: '#64748B' },
]

/**
 * Select options — the same list minus `color`, which the dropdown does not
 * use. Derived rather than hand-maintained so the two cannot drift.
 *
 * Alphabetised, unlike `EVENT_CATEGORIES` above: the select is searchable, so
 * a predictable A-Z order beats the source list's grouping-by-kind once the
 * admin is typing to filter. The source order still drives the badge palette.
 */
export const EVENT_CATEGORY_OPTIONS = EVENT_CATEGORIES.map(
  ({ label, value }) => ({ label, value })
).sort((a, b) => a.label.localeCompare(b.label))

/** Fallback fill for a category the client does not know about. */
const UNKNOWN_CATEGORY_COLOR = '#64748B'

/**
 * The badge for an event's category, or null when it has none.
 *
 * An unrecognised category still renders — the backend calls this list
 * *suggested*, so a value we have no colour for is displayed with the neutral
 * "Other" grey rather than dropped. Matching is case-insensitive and
 * whitespace-tolerant so a stored "practice" still finds its colour.
 */
export const getEventCategory = (
  category: string | null | undefined
): ResolvedEventCategory | null => {
  const trimmed = category?.trim()
  if (!trimmed) {
    return null
  }

  const known = EVENT_CATEGORIES.find(
    (entry) => entry.value.toLowerCase() === trimmed.toLowerCase()
  )

  const resolved = known ?? {
    label: trimmed,
    value: trimmed,
    color: UNKNOWN_CATEGORY_COLOR,
  }

  return { ...resolved, textColor: getContrastTextColor(resolved.color) }
}
